import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';

const IntroPage = ({ onEnter }) => {
  const isMobile = useIsMobile();
  const [ripples, setRipples] = useState([]);

  const handleEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onEnter();
  }, [onEnter]);

  // Ripples — fewer on mobile
  useEffect(() => {
    if (isMobile) return; // skip ripples on mobile entirely
    const interval = setInterval(() => {
      setRipples(prev => [
        ...prev.slice(-3),
        { id: Date.now() }
      ]);
    }, 3000);
    return () => clearInterval(interval);
  }, [isMobile]);

  const particleCount = isMobile ? 4 : 20;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#0A0208' }}
      data-testid="intro-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, #2D0A1E 0%, #1A0510 40%, #0A0208 100%)',
      }} />

      {/* Background glow — no blur on mobile, use opacity gradient instead */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: isMobile ? '400px' : '600px',
          height: isMobile ? '400px' : '600px',
          background: 'radial-gradient(circle, rgba(255,0,255,0.15) 0%, transparent 70%)',
          ...(isMobile ? {} : { filter: 'blur(60px)' }),
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Floating particles — reduced count, no boxShadow on mobile */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(particleCount)].map((_, i) => (
          <motion.div
            key={`intro-p-${i}`}
            className="absolute rounded-full"
            style={{
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              background: ['#FF00FF', '#FFD1DC', '#FF69B4', '#CCFF00'][i % 4],
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              ...(isMobile ? {} : { boxShadow: `0 0 ${6 + Math.random() * 6}px currentColor` }),
            }}
            animate={{
              y: [0, -(15 + Math.random() * 25), 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Title */}
      <motion.div
        className="relative z-10 text-center mb-8 md:mb-12 px-4"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <h1
          className="font-heading text-5xl sm:text-6xl lg:text-8xl text-white uppercase tracking-tight"
          style={{ textShadow: '0 0 60px rgba(255,0,255,0.5)' }}
        >
          <span className="text-gradient-pink">ACID</span>{' '}
          <span className="text-white">RANI</span>
        </h1>
        <motion.p
          className="mt-4 font-handwritten text-[#CCFF00] text-base md:text-lg tracking-wide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          the gateway to liberation
        </motion.p>
      </motion.div>

      {/* Clickable artwork wrapper */}
      <motion.div
        className="relative z-10 cursor-pointer"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
        onClick={handleEnter}
        onTouchEnd={handleEnter}
        role="button"
        tabIndex={0}
        aria-label="Enter the world of Acid Rani"
        data-testid="intro-artwork-trigger"
      >
        <svg
          viewBox="0 0 300 480"
          className="w-[200px] h-[320px] sm:w-[260px] sm:h-[416px] md:w-[320px] md:h-[512px]"
          style={{
            touchAction: 'manipulation',
            ...(isMobile ? {} : { filter: 'drop-shadow(0 0 40px rgba(255,0,255,0.35))' }),
          }}
          data-testid="intro-vulva-art"
        >
          <defs>
            <radialGradient id="outerGlow" cx="50%" cy="50%" r="55%">
              <stop offset="0%" stopColor="#FF69B4" stopOpacity="0.5" />
              <stop offset="60%" stopColor="#FF00FF" stopOpacity="0.2" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="petalGrad1" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FFB6C1" stopOpacity="0.85" />
              <stop offset="50%" stopColor="#FF69B4" stopOpacity="0.65" />
              <stop offset="100%" stopColor="#C71585" stopOpacity="0.4" />
            </radialGradient>
            <radialGradient id="petalGrad2" cx="50%" cy="45%" r="50%">
              <stop offset="0%" stopColor="#FFD1DC" stopOpacity="0.9" />
              <stop offset="40%" stopColor="#FF69B4" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FF1493" stopOpacity="0.4" />
            </radialGradient>
            <radialGradient id="innerGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FFE4E1" stopOpacity="1" />
              <stop offset="50%" stopColor="#FFB6C1" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#FF69B4" stopOpacity="0.6" />
            </radialGradient>
            <radialGradient id="coreGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
              <stop offset="40%" stopColor="#FFD1DC" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FF69B4" stopOpacity="0.5" />
            </radialGradient>
            {/* Only use SVG filters on desktop */}
            {!isMobile && (
              <>
                <filter id="softGlow">
                  <feGaussianBlur stdDeviation="8" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="innerGlow">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </>
            )}
          </defs>

          {/* Ambient ripples — desktop only */}
          {!isMobile && ripples.map(r => (
            <motion.circle
              key={r.id}
              cx={150} cy={240} r="5"
              fill="none" stroke="#FF69B4" strokeWidth="0.5"
              initial={{ r: 5, opacity: 0.4 }}
              animate={{ r: 100, opacity: 0 }}
              transition={{ duration: 3, ease: 'easeOut' }}
            />
          ))}

          {/* Outer aura */}
          <ellipse cx="150" cy="240" rx="140" ry="220" fill="url(#outerGlow)" />

          {/* Outermost petals — static path on mobile, animated on desktop */}
          <motion.path
            d="M150,30 Q230,30 260,120 Q290,210 270,300 Q250,400 150,460 Q50,400 30,300 Q10,210 40,120 Q70,30 150,30 Z"
            fill="url(#petalGrad1)"
            filter={isMobile ? undefined : "url(#softGlow)"}
            animate={isMobile ? { opacity: [0.8, 0.9, 0.8] } : {
              d: [
                "M150,30 Q230,30 260,120 Q290,210 270,300 Q250,400 150,460 Q50,400 30,300 Q10,210 40,120 Q70,30 150,30 Z",
                "M150,25 Q235,28 265,118 Q295,212 275,305 Q252,405 150,465 Q48,405 25,305 Q5,212 35,118 Q65,28 150,25 Z",
                "M150,30 Q230,30 260,120 Q290,210 270,300 Q250,400 150,460 Q50,400 30,300 Q10,210 40,120 Q70,30 150,30 Z",
              ],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Inner labia left — static on mobile */}
          <motion.path
            d="M150,55 Q210,70 230,150 Q250,230 235,320 Q215,400 150,440 Q130,400 120,320 Q110,250 115,180 Q120,100 150,55 Z"
            fill="url(#petalGrad2)"
            filter={isMobile ? undefined : "url(#innerGlow)"}
            opacity="0.85"
            animate={isMobile ? { opacity: [0.75, 0.9, 0.75] } : {
              d: [
                "M150,55 Q210,70 230,150 Q250,230 235,320 Q215,400 150,440 Q130,400 120,320 Q110,250 115,180 Q120,100 150,55 Z",
                "M150,50 Q215,65 235,148 Q255,232 240,325 Q218,405 150,445 Q128,405 115,325 Q105,252 110,178 Q115,95 150,50 Z",
                "M150,55 Q210,70 230,150 Q250,230 235,320 Q215,400 150,440 Q130,400 120,320 Q110,250 115,180 Q120,100 150,55 Z",
              ],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          />

          {/* Inner labia right — static on mobile */}
          <motion.path
            d="M150,55 Q90,75 70,155 Q50,235 65,325 Q85,405 150,440 Q170,405 180,325 Q190,250 185,175 Q180,95 150,55 Z"
            fill="url(#petalGrad2)"
            opacity="0.75"
            animate={isMobile ? { opacity: [0.65, 0.8, 0.65] } : {
              d: [
                "M150,55 Q90,75 70,155 Q50,235 65,325 Q85,405 150,440 Q170,405 180,325 Q190,250 185,175 Q180,95 150,55 Z",
                "M150,50 Q85,72 65,152 Q45,237 60,328 Q82,408 150,445 Q172,408 185,328 Q195,252 190,172 Q185,92 150,50 Z",
                "M150,55 Q90,75 70,155 Q50,235 65,325 Q85,405 150,440 Q170,405 180,325 Q190,250 185,175 Q180,95 150,55 Z",
              ],
            }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
          />

          {/* Central slit — static on mobile */}
          <motion.path
            d="M150,80 Q195,100 205,180 Q215,260 200,340 Q185,410 150,430 Q115,410 100,340 Q85,260 95,180 Q105,100 150,80 Z"
            fill="url(#innerGrad)"
            animate={isMobile ? { opacity: [0.8, 0.95, 0.8] } : {
              d: [
                "M150,80 Q195,100 205,180 Q215,260 200,340 Q185,410 150,430 Q115,410 100,340 Q85,260 95,180 Q105,100 150,80 Z",
                "M150,75 Q198,98 208,178 Q218,262 203,342 Q188,412 150,435 Q112,412 97,342 Q82,262 92,178 Q102,98 150,75 Z",
                "M150,80 Q195,100 205,180 Q215,260 200,340 Q185,410 150,430 Q115,410 100,340 Q85,260 95,180 Q105,100 150,80 Z",
              ],
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />

          {/* Deep inner passage */}
          <motion.ellipse
            cx="150" cy="240" rx="35" ry="90"
            fill="#C71585" opacity="0.45"
            animate={{ opacity: [0.4, 0.55, 0.4] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Inner core */}
          <motion.ellipse
            cx="150" cy="240" rx="18" ry="55"
            fill="url(#coreGrad)" opacity="0.6"
            animate={{ opacity: [0.55, 0.8, 0.55] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Vertical slit */}
          <line x1="150" y1="60" x2="150" y2="445"
            stroke="rgba(199,21,133,0.2)" strokeWidth="1.5" strokeLinecap="round" />

          {/* Clitoris nub */}
          <motion.path
            d="M150,38 Q165,35 170,45 Q175,55 165,62 Q155,68 145,68 Q135,68 125,62 Q115,55 120,45 Q125,35 140,38 Z"
            fill="#FF69B4" opacity="0.6"
            animate={{ opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <motion.circle cx="150" cy="50" r="10" fill="#FFD1DC"
            animate={{ opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <circle cx="150" cy="50" r="5" fill="#FFFFFF" opacity="0.85" />

          {/* G-spot */}
          <motion.circle cx="150" cy="200" r="8" fill="#CCFF00" opacity="0.55"
            animate={{ opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </svg>
      </motion.div>

      {/* Instruction text */}
      <motion.div
        className="relative z-10 mt-8 md:mt-12 text-center px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
      >
        <p className="hidden md:block text-[#FFD1DC]/80 font-body text-sm tracking-widest uppercase">
          Click the{' '}
          <span className="text-[#FF00FF] font-semibold">sacred spot</span>{' '}
          to enter her world
        </p>
        <p className="md:hidden text-[#FFD1DC]/80 font-body text-sm tracking-widest uppercase">
          Tap the{' '}
          <span className="text-[#FF00FF] font-semibold">glowing point</span>{' '}
          to enter
        </p>
        <motion.p
          className="mt-3 font-handwritten text-[#FF69B4]/50 text-xs"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          find the clitoris... or the g-spot
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export default IntroPage;
